import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableMap,
  ObservableSet,
  runInAction,
  toJS,
} from "mobx";

import { SpaceAccountObservable } from "@/store/space-account/observables/SpaceAccountObservable";
import { api } from "@/modules/api";
import { CacheKey } from "@/modules/cache/constants";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { BaseError, RuntimeAssertionError } from "@/domains/errors";
import { Uuid } from "@/domains/global/identifiers";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { objectModule } from "@/modules/object";
import { uuidModule } from "@/modules/uuid";
import { logger } from "@/modules/logger";
import type {
  MemAgentAccess,
  MemAgentTierOverride,
} from "@/store/space-account/observables/SpaceAccountObservable";

type SpaceAccountResponseWithMemAgentSettings = {
  mem_agent_tier_override?: MemAgentTierOverride | null;
  mem_agent_access?: MemAgentAccess | null;
};

type SpaceAccountApiData = SpaceAccountResponseWithMemAgentSettings & {
  id: Uuid;
  account_id: Uuid;
  space_id: Uuid;
  profile_email_address: string;
  profile_display_name: string;
  profile_photo_url?: string | null;
};

type CachedSpaceAccountWithMemAgentSettings = {
  id: Uuid;
  accountId: Uuid;
  spaceId: Uuid;
  profileEmailAddress: string;
  profileDisplayName: string;
  profilePhotoUrl?: string | null;
  memAgentTierOverride?: MemAgentTierOverride | null;
  memAgentAccess?: MemAgentAccess | null;
};

export interface ISpaceAccountStore {
  myPersonalSpaceAccount: SpaceAccountObservable | undefined;
}

export class AppStoreSpaceAccountStore extends AppSubStore implements ISpaceAccountStore {
  spaceAccountMap: ObservableMap<Uuid, SpaceAccountObservable>;
  fetchMySpaceAccountsState: AsyncResult<ObservableSet<Uuid>>;
  /**
   * Advances for each fetch and reset so stale async work cannot restore state or cache data.
   */
  private fetchGeneration = 0;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.spaceAccountMap = new ObservableMap();
    this.fetchMySpaceAccountsState = asyncResultModule.setInitial();

    makeObservable<AppStoreSpaceAccountStore, "fetchGeneration">(this, {
      spaceAccountMap: observable,
      fetchMySpaceAccountsState: observable,
      fetchGeneration: false,

      getSpaceAccount: false,
      getSpaceAccountBySpaceId: false,
      addSpaceAccount: action,
      fetchMySpaceAccounts: action,
      persistMySpaceAccountsToCache: action,
      hydrateMySpacesAccountsFromCache: action,
      initialize: action,

      myPersonalSpaceAccount: computed,
      myPersonalSpaceAccountId: computed,
      mySpaceAccountSpaceIds: computed,
      mySpaceAccountIds: computed,
      mySpaceAccounts: computed,
      resetState: action,
    });
  }

  getSpaceAccount = (spaceAccountId: Uuid): SpaceAccountObservable | undefined => {
    return this.spaceAccountMap.get(spaceAccountId);
  };

  getSpaceAccountBySpaceId = (spaceId: Uuid): SpaceAccountObservable | undefined => {
    const values = this.spaceAccountMap.values();

    const spaceAccount = Array.from(values).find(value => value.spaceId === spaceId);

    return spaceAccount;
  };

  addSpaceAccount = ({ spaceAccount }: { spaceAccount: SpaceAccountObservable }) => {
    runInAction(() => {
      this.spaceAccountMap.set(spaceAccount.id, spaceAccount);
      this.fetchMySpaceAccountsState.data?.add(spaceAccount.id);
    });
  };

  fetchMySpaceAccounts = async () => {
    const generation = ++this.fetchGeneration;

    runInAction(() => {
      this.fetchMySpaceAccountsState.called = true;
      this.fetchMySpaceAccountsState.error = undefined;
      this.fetchMySpaceAccountsState.loading = true;
    });

    try {
      const result = await api.get("/v1/me/space-accounts", {});

      if (generation !== this.fetchGeneration) {
        return;
      }

      if (!result.data) {
        throw new BaseError({
          message: "Failed to fetch current account space accounts.",
          info: {
            result: objectModule.safeAsJson(result),
          },
        });
      }

      const spaceAccountDataArray = result.data as SpaceAccountApiData[];
      if (spaceAccountDataArray.length === 0) {
        throw new BaseError({
          message: "Failed to fetch current account space accounts: response was empty.",
        });
      }

      const spaceAccounts = spaceAccountDataArray.map(spaceAccountData =>
        SpaceAccountObservable.fromApiData({
          data: spaceAccountData,
        })
      );

      runInAction(() => {
        spaceAccounts.forEach(spaceAccount => {
          this.spaceAccountMap.set(spaceAccount.id, spaceAccount);
        });

        const spaceAccountIds = spaceAccounts.map(spaceAccount => spaceAccount.id);
        this.fetchMySpaceAccountsState.data = new ObservableSet(spaceAccountIds);
      });

      await this.persistMySpaceAccountsToCache(spaceAccounts);
    } catch (unknownErr) {
      if (generation !== this.fetchGeneration) {
        return;
      }

      const err =
        unknownErr instanceof BaseError
          ? unknownErr
          : new BaseError({
              message: "Failed to fetch current account space accounts.",
              originalError: unknownErr as Error,
            });

      runInAction(() => {
        this.fetchMySpaceAccountsState.error = err;
      });

      logger.warn({
        message: "[SpaceAccountStore] Failed to refresh space accounts.",
        info: { err: objectModule.safeErrorAsJson(err) },
      });
    } finally {
      if (generation === this.fetchGeneration) {
        runInAction(() => {
          this.fetchMySpaceAccountsState.loading = false;
        });
      }
    }
  };

  persistMySpaceAccountsToCache = async (spaceAccounts: SpaceAccountObservable[]) => {
    try {
      await this.store.accountCache.set(
        CacheKey.MySpaceAccounts,
        spaceAccounts.map(spaceAccount => toJS(spaceAccount))
      );
    } catch (unknownErr) {
      logger.warn({
        message: "[SpaceAccountStore] Failed to persist refreshed space accounts.",
        info: { err: objectModule.safeErrorAsJson(unknownErr as Error) },
      });
    }
  };

  hydrateMySpacesAccountsFromCache = async (
    generation: number = this.fetchGeneration
  ): Promise<boolean> => {
    const mySpaceAccounts = (await this.store.accountCache.get(CacheKey.MySpaceAccounts)) as
      | CachedSpaceAccountWithMemAgentSettings[]
      | undefined;

    if (generation !== this.fetchGeneration || !mySpaceAccounts) {
      return false;
    }

    const spaceAccounts = mySpaceAccounts.map(spaceAccountData => {
      const spaceAccount = new SpaceAccountObservable({
        id: spaceAccountData.id,
        accountId: spaceAccountData.accountId,
        spaceId: spaceAccountData.spaceId,
        profileEmailAddress: spaceAccountData.profileEmailAddress,
        profileDisplayName: spaceAccountData.profileDisplayName,
        profilePhotoUrl: spaceAccountData.profilePhotoUrl ?? null,
        memAgentTierOverride: spaceAccountData.memAgentTierOverride ?? null,
        memAgentAccess: spaceAccountData.memAgentAccess ?? null,
      });

      return spaceAccount;
    });

    runInAction(() => {
      spaceAccounts.forEach(spaceAccount => {
        this.spaceAccountMap.set(spaceAccount.id, spaceAccount);
      });

      const spaceAccountIds = spaceAccounts.map(spaceAccount => spaceAccount.id);

      this.fetchMySpaceAccountsState.data = new ObservableSet(spaceAccountIds);
    });

    return true;
  };

  initialize = async () => {
    const fetchMySpaceAccountsPromise = this.fetchMySpaceAccounts();
    const generation = this.fetchGeneration;
    const hydrationSuccess = await this.hydrateMySpacesAccountsFromCache(generation);

    if (generation !== this.fetchGeneration) {
      return;
    }

    /**
     * If we were able to hydrate from cache, we don't need to wait for the API call.
     */
    if (hydrationSuccess) {
      return;
    }

    await fetchMySpaceAccountsPromise;

    if (this.fetchMySpaceAccountsState.error) {
      throw new RuntimeAssertionError({
        message: "[SpaceAccountStore.initialize] Failed.",
        info: { originalError: objectModule.safeErrorAsJson(this.fetchMySpaceAccountsState.error) },
      });
    }
  };

  get myPersonalSpaceAccountId(): Uuid {
    const personalSpaceAccount = this.myPersonalSpaceAccount;

    if (!personalSpaceAccount) {
      /**
       * @todo - This shouldn't be necessary, but keeping this here until we have time
       * to verify the boot flow works well. We should technically just be able to
       * throw an error, because this should always be loaded.
       */
      const personalSpaceId = this.store.spaces.myPersonalSpaceId;
      const fallback = uuidModule.resolveSpaceAccountUuid({
        spaceId: personalSpaceId,
        accountId: this.store.account.myAccountId,
      });

      return fallback;
    }

    return personalSpaceAccount.id;
  }

  get myPersonalSpaceAccount() {
    const personalSpaceId = this.store.spaces.myPersonalSpaceId;

    const personalSpaceAccount = this.mySpaceAccounts.find(
      spaceAccount => spaceAccount.spaceId === personalSpaceId
    );

    return personalSpaceAccount;
  }

  get mySpaceAccountSpaceIds(): Uuid[] {
    const spaceAccountSpaceIds = this.mySpaceAccounts.map(spaceAccount => spaceAccount.spaceId);

    return spaceAccountSpaceIds;
  }

  get mySpaceAccountIds(): Uuid[] {
    const spaceAccountIds = this.fetchMySpaceAccountsState.data;

    if (!spaceAccountIds) {
      throw new RuntimeAssertionError({
        message:
          "[SpaceAccountStore.mySpaceAccountIds] spaceAccountIds was not set - make sure that `initialize` was called.",
      });
    }

    if (!spaceAccountIds.size) {
      throw new RuntimeAssertionError({
        message:
          "[SpaceAccountStore.mySpaceAccountIds] spaceAccountIds was empty - make sure that `initialize` was called.",
      });
    }

    return Array.from(spaceAccountIds);
  }

  get mySpaceAccounts(): SpaceAccountObservable[] {
    const spaceAccounts = this.mySpaceAccountIds.map(spaceAccountId => {
      const spaceAccount = this.getSpaceAccount(spaceAccountId);

      if (!spaceAccount) {
        throw new RuntimeAssertionError({
          message:
            "[SpaceAccountStore.mySpaceAccounts] spaceAccount was not set - make sure that `initialize` was called.",
        });
      }

      return spaceAccount;
    });

    return spaceAccounts;
  }

  resetState = () => {
    this.fetchGeneration += 1;

    runInAction(() => {
      this.spaceAccountMap = new ObservableMap();
      this.fetchMySpaceAccountsState = asyncResultModule.setInitial();
    });
  };
}
